import { useEffect, useRef, useState } from 'react';

import { isSafari } from '../utils/navigator';

export const DEFAULT_AUDIO_VOLUME = isSafari ? 0.5 : 0.2;

const useAutoFade = ({
  delay,
  eventListenerDependency,
  startInvisible = false,
  callback,
}: {
  delay: number;
  eventListenerDependency: string | boolean;
  startInvisible?: boolean;
  callback?: () => void;
}) => {
  const [isInvisible, setIsInvisible] = useState(startInvisible);
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const clear = () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }
    };

    const scheduleHide = () => {
      setIsInvisible(false);
      clear();
      timeout.current = setTimeout(() => {
        setIsInvisible(true);
        if (callback) callback();
      }, delay);
    };

    const hideNow = () => {
      clear();
      setIsInvisible(true);
      if (callback) callback();
    };

    if (startInvisible) {
      hideNow();
    } else {
      scheduleHide();
    }

    const onInteract = () => scheduleHide();
    window.addEventListener('mousemove', onInteract);
    window.addEventListener('touch', onInteract); // Mobile and touch
    window.addEventListener('drag', onInteract); // Mobile and touch
    window.addEventListener('click', onInteract); // Mobile and touch
    window.addEventListener('scroll', onInteract); // Mobile and touch
    const fsEl = document.fullscreenElement;
    if (fsEl) {
      fsEl.addEventListener('mousemove', onInteract);
    }
    return () => {
      clear();
      window.removeEventListener('mousemove', onInteract);
      window.removeEventListener('touch', onInteract); // Mobile and touch
      window.removeEventListener('drag', onInteract); // Mobile and touch
      window.removeEventListener('click', onInteract); // Mobile and touch
      window.removeEventListener('scroll', onInteract); // Mobile and touch
      if (fsEl) {
        fsEl.removeEventListener('mousemove', onInteract);
      }
    };
  }, [eventListenerDependency, delay, startInvisible, callback]);

  return { isInvisible };
};

export default useAutoFade;
